// 2026-05-26 - FormuLearning - Step 카드 셸 (접기/펼치기, barColor, Save & Continue)
// 2026-09-01 수정 - 이슈 #58 S10(#70): 공통 카드 규칙 3건.
//   ① `M84-48` **읽기 전용 모드**(`readOnly`) 신설 — Step 3 을 넘어간 뒤 이전 STEP 을
//      잠근다. `locked`(하드 게이트)와 다르다: 콘텐츠는 그대로 보이고 **입력만** 잠근다.
//      지나간 스텝의 값은 뒤에서 확인해야 하므로 감추면 안 된다.
//   ② `M84-50` **접힌 카드에서 AI Summary 를 숨긴다**(사용자 확정 Q-260901-01).
//      종전(#40)은 접힘·펼침 동일 노출이었으나, 진행 중 스텝만 열리면 접힌 카드 5장의
//      요약이 쌓여 목록이 읽히지 않는다.
// 2026-09-23 수정 - 이슈 #82: **진행 중 작업 잠금.** 다른 스텝의 장시간 작업(`busyStore`)이 진행 중이면
//   ① 본문을 읽기 전용과 같은 `fieldset disabled` 로 통째로 잠그고 투명 덮개가 클릭을 받아 안내한다
//     (비활성 컨트롤은 클릭 이벤트를 내지 않아 버튼 쪽에서는 안내할 수 없다)
//   ② Save and continue 는 흐리게 두고 누르면 안내한다(작업 소유 카드 포함)
//   ③ 작업 소유 카드의 개폐 토글을 잠근다 — 접으면 언마운트되어 작업이 확인 없이 끊긴다.
//      다른 카드의 개폐는 열람 조작이라 열어 둔다(UI/UX v2.3 «열람 조작은 잠금 밖»).
// 2026-11-03 수정 - 이슈 #114: viewer 는 Save and continue 비활성(툴팁 «조회 전용 계정»).
// 파일 위치: frontend/src/components/layout/CardShell.tsx

import { useState } from 'react'
import AiSummary from '../feedback/AiSummary'
import { useBusyStore, busyMessage, blockIfBusy } from '../../stores/busyStore'
import { toast } from '../../stores/toastStore'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface CardShellProps {
  stepNumber: number
  title: string
  barColor: 'purple' | 'teal'
  children: React.ReactNode
  onSave?: () => void
  saving?: boolean
  // 2026-07-09 - 게이팅 (이슈 #33 REQ-B6). locked=하드 게이트(콘텐츠 대신 안내), warn=소프트 게이트(경고 배너)
  locked?: string
  warn?: string
  // 2026-09-01 - 역방향 잠금 (이슈 #58 S10/#70, M84-48). 문구를 주면 읽기 전용이 된다 —
  //   콘텐츠는 그대로 보이되 입력·저장이 잠긴다. `locked`처럼 내용을 가리지 않는다.
  readOnly?: string
  // 2026-09-01 - 잠금의 범위 (사용자 확정 260901).
  //   `'card'`(기본) — 셸이 콘텐츠 **전체**를 잠근다. 대부분의 스텝이 이쪽이다.
  //   `'self'` — 셸은 **안내만** 하고 어디를 잠글지는 카드가 정한다. 잠금에서 빼야 할
  //     영역이 있는 스텝용이다(Step 3 의 대조약 Lot — Step 4 가 run 마다 고르는 대상이라
  //     Step 3 소관이면서도 Step 4 진행 중에 열려 있어야 한다).
  readOnlyScope?: 'card' | 'self'
  // 2026-07-09 - Autosave OFF 미저장 배지 (이슈 #33 4단계)
  dirty?: boolean
  // 2026-07-14 - 헤더 직하 사용자 가이드 박스 문구 (이슈 #23 F-12). 세션 단위 dismiss.
  guide?: string[]
  // 2026-07-18 - 프로젝트 로드 시 초기 접힘 상태. COMPLETED 스텝은 접힌 채로 연다.
  defaultCollapsed?: boolean
  // 2026-07-18 - AI Summary (이슈 #40). 접힘 여부와 무관하게 헤더 직하에 노출. 영문 본문 + 한글 hover.
  summaryEn?: string
  summaryKo?: string
}

// 좌측 4px 바 — 진한 액센트(장식)
const BAR_COLORS = {
  purple: 'var(--color-accent-purple)',
  teal: 'var(--color-accent-teal)',
}

// 2026-06-19 - Save 버튼 배경 — 버튼 액센트 변수(라이트 파스텔, 다크는 동일). 이슈 #9
const BTN_COLORS = {
  purple: 'var(--btn-accent-purple)',
  teal: 'var(--btn-accent-teal)',
}

export default function CardShell({ stepNumber, title, barColor, children, onSave, saving, locked, warn, readOnly, readOnlyScope = 'card', dirty, guide, defaultCollapsed, summaryEn, summaryKo }: CardShellProps) {
  // 2026-07-18 - 초기 접힘은 defaultCollapsed(프로젝트 로드 시 COMPLETED 여부)로 결정.
  // 이후 사용자의 수동 토글은 세션 내 유지(프로젝트 전환 시 상위 key로 리마운트되어 재계산).
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false)
  // 2026-07-14 - 가이드 박스 세션 단위 dismiss (이슈 #23). 지속 저장 안 함.
  const [guideDismissed, setGuideDismissed] = useState(false)
  // 2026-09-23 - 진행 중 작업 잠금 (이슈 #82). 소유 카드는 본문을 잠그지 않는다 — 카드 안 잠금은 그 카드 몫(BR-S1-07).
  const busyTask = useBusyStore((s) => s.task)
  const busyOwner = busyTask?.step === stepNumber
  const busyLocked = busyTask && !busyOwner ? busyMessage(busyTask) : undefined
  // viewer(#114) — Save and continue 는 숨기지 않고 비활성 + «조회 전용 계정». 스텝 이동은 좌측 단계 목록·카드 개폐로 한다.
  const canWrite = useCanWrite()
  const toggleCollapsed = () => {
    if (busyOwner && !collapsed) {
      toast(`${busyTask.label} 작업 중에는 카드를 접을 수 없습니다. 접으면 작업이 중단됩니다.`, 'warn')
      return
    }
    setCollapsed(!collapsed)
  }
  const handleSave = () => {
    if (blockIfBusy()) return
    onSave?.()
  }

  return (
    <section
      id={`step-${stepNumber}`}
      className="rounded-lg"
      style={{ backgroundColor: 'var(--color-bg-card)', boxShadow: 'var(--elev-1)' }}
    >
      <div className="flex w-full items-center gap-3 px-5 py-4">
        <button
          onClick={toggleCollapsed}
          className="flex flex-1 items-center gap-3 text-left"
        >
          <span
            className="block rounded-sm"
            style={{ width: 4, height: 16, backgroundColor: BAR_COLORS[barColor] }}
          />
          <span className="text-xs font-medium text-text-secondary">Step {stepNumber}</span>
          <span className="text-sm font-semibold text-text-primary">{title}</span>
          {dirty && (
            // Autosave OFF 미저장 표시 (이슈 #33 4단계)
            <span className="text-[10px] font-medium" style={{ color: 'var(--color-accent-purple)' }} title="미저장 편집 있음 — Save and continue로 저장">
              ● 미저장
            </span>
          )}
          <span className="text-xs text-text-secondary">{collapsed ? '▼' : '▲'}</span>
        </button>
        {onSave && !collapsed && !locked && !readOnly && (
          <button
            onClick={handleSave}
            disabled={saving || !canWrite}
            title={canWrite ? undefined : READ_ONLY_TITLE}
            className={`shrink-0 rounded-md px-3 py-1.5 text-[11px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-50${busyTask ? ' opacity-50' : ''}`}
            style={{ backgroundColor: BTN_COLORS[barColor], color: '#1a1b1e' }}
          >
            {saving ? 'Saving...' : 'Save and continue'}
          </button>
        )}
      </div>
      {/* 2026-07-18 - AI Summary (이슈 #40). 헤더 직하에 렌더한다. 데이터는 children 이 아니라
          summaryStore(중앙 populate)에서 오므로 카드 내용과 독립이다.
          2026-09-01 - 이슈 #58 S10(M84-50): **펼친 카드에서만** 보인다(사용자 확정 Q-260901-01).
          진행 중 스텝만 열리는 규칙에서 접힌 카드의 요약은 목록의 가독성을 해친다. */}
      {!collapsed && (summaryEn || summaryKo) && (
        <div className="px-5 pb-1">
          <AiSummary en={summaryEn} ko={summaryKo} />
        </div>
      )}
      {!collapsed && (
        <div className="border-t px-5 pb-5 pt-4" style={{ borderColor: 'var(--border-subtle)' }}>
          {locked ? (
            // 하드 게이트 — 선행 조건 미충족 시 콘텐츠 대신 안내 (이슈 #33 REQ-B6)
            <div
              className="flex items-center gap-2 rounded-md px-4 py-6 text-xs"
              style={{ background: 'var(--bg-input)', color: 'var(--color-text-secondary)' }}
            >
              <span>🔒</span>
              <span>{locked}</span>
            </div>
          ) : (
            <>
              {readOnly && (
                // 역방향 잠금 안내 (이슈 #58 S10/#70, M84-48). 왜 입력이 안 되는지 적지 않으면
                // 사용자는 화면이 고장난 것으로 읽는다.
                <div
                  className="mb-3 flex items-start gap-2 rounded-md px-3 py-2 text-[11px] leading-relaxed"
                  data-testid={`step-readonly-${stepNumber}`}
                  style={{ background: 'var(--bg-input)', color: 'var(--color-text-secondary)' }}
                >
                  <span aria-hidden className="shrink-0">🔒</span>
                  <span>{readOnly}</span>
                </div>
              )}
              {warn && (
                // 소프트 게이트 — 진행은 허용, 미충족 안내만 (이슈 #33 REQ-B6)
                <div
                  className="mb-3 rounded-md px-3 py-2 text-[11px]"
                  style={{ background: 'var(--btn-accent-purple)', color: 'var(--color-text-body)' }}
                >
                  ⚠ {warn}
                </div>
              )}
              {guide && guide.length > 0 && !guideDismissed && (
                // 헤더 직하 사용자 가이드 박스 (이슈 #23 F-12). 세션 단위 dismiss.
                <div
                  className="mb-3 flex items-start gap-2 rounded-md px-3 py-2.5 text-[11px] leading-relaxed"
                  style={{ background: 'var(--bg-input)', color: 'var(--color-text-secondary)' }}
                >
                  <span aria-hidden className="mt-px shrink-0">💡</span>
                  <div className="flex-1 space-y-1">
                    {guide.map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                  <button
                    onClick={() => setGuideDismissed(true)}
                    className="shrink-0 leading-none opacity-60 transition-opacity hover:opacity-100"
                    title="가이드 닫기"
                    aria-label="가이드 닫기"
                  >
                    ✕
                  </button>
                </div>
              )}
              {/* 읽기 전용은 `fieldset disabled` 한 겹으로 건다(M84-48). 스텝 카드마다
                  입력·저장 버튼이 흩어져 있어 개별로 잠그면 빠뜨리는 자리가 생긴다 —
                  브라우저가 자손 폼 컨트롤을 통째로 비활성화하는 편이 누락이 없다.
                  `readOnlyScope='self'` 면 걸지 않는다 — 그 카드가 스스로 범위를 정한다.
                  (중첩 fieldset 으로는 되살릴 수 없다: 비활성 fieldset 의 자손은 규격상
                   전부 비활성이라 «전체 잠그고 일부만 열기» 가 성립하지 않는다.) */}
              {busyLocked ? (
                // 진행 중 작업 잠금(#82). 읽기 전용보다 넓다 — Step 3 의 대조약 Lot 예외('self')도 잠근다.
                <div className="relative" data-testid={`step-busy-${stepNumber}`}>
                  <fieldset disabled className="m-0 min-w-0 border-0 p-0 opacity-60">{children}</fieldset>
                  <div
                    className="absolute inset-0 z-10 cursor-not-allowed"
                    title={busyLocked}
                    onClick={() => toast(busyLocked, 'warn')}
                  />
                </div>
              ) : readOnly && readOnlyScope === 'card' ? (
                <fieldset disabled className="m-0 min-w-0 border-0 p-0">{children}</fieldset>
              ) : children}
              {onSave && !readOnly && (
                <div className="mt-4 flex justify-end border-t pt-3" style={{ borderColor: 'var(--border-subtle)' }}>
                  <button
                    onClick={handleSave}
                    disabled={saving || !canWrite}
            title={canWrite ? undefined : READ_ONLY_TITLE}
                    className={`rounded-md px-4 py-2 text-xs font-semibold transition-opacity hover:opacity-90 disabled:opacity-50${busyTask ? ' opacity-50' : ''}`}
                    style={{ backgroundColor: BTN_COLORS[barColor], color: '#1a1b1e' }}
                  >
                    {saving ? 'Saving...' : 'Save and continue'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  )
}
